import type { PropertyDetailListing } from '../types';
import type { Floorplan, Unit } from '../UnitsModule/UnitTable';

export type MappedUnit = { engrainMapUnitId: string; unit: Unit; floorplan: Floorplan };
export type MapFloor = { id: string; label: string };
export type MapUnit = { id: string; asset_id: string; floor_id: string };
export type LocatedUnit = MappedUnit & { floorId: string };

export function getMappedUnits(listing: PropertyDetailListing): Array<MappedUnit> {
  if (
    !listing.building ||
    listing.isUnit ||
    listing.address?.hideStreet ||
    (listing.active === false && listing.isAllowedToRetain === false) ||
    !listing.propertyMap?.mapId?.trim() ||
    !listing.propertyMap.assetId?.trim()
  )
    return [];

  const units = ((listing.floorplans as Array<Floorplan> | undefined) ?? []).flatMap((floorplan) =>
    (floorplan.units ?? []).map((unit) => ({ unit, floorplan })),
  );
  const mappings = listing.propertyMap.unitMappings ?? [];
  const result = new Map<string, MappedUnit>();
  for (const mapping of mappings) {
    const matches = units.filter(({ unit }) => unit.modelId === mapping.modelId);
    if (!mapping.engrainMapUnitId?.trim() || matches.length !== 1) continue;
    if (
      mappings.some(
        (other) =>
          (other.engrainMapUnitId === mapping.engrainMapUnitId && other.modelId !== mapping.modelId) ||
          (other.modelId === mapping.modelId && other.engrainMapUnitId !== mapping.engrainMapUnitId),
      )
    )
      continue;
    result.set(mapping.engrainMapUnitId, { ...matches[0], engrainMapUnitId: mapping.engrainMapUnitId });
  }
  return [...result.values()];
}

export function locateMappedUnits(
  mapped: Array<MappedUnit>,
  units: Array<MapUnit>,
  floors: Array<MapFloor>,
  artworkIds: Set<string>,
  assetId: string,
): Array<LocatedUnit> {
  const byId = new Map(units.filter((unit) => unit.asset_id === assetId).map((unit) => [unit.id, unit]));
  return mapped.flatMap((match) => {
    const floorId = byId.get(match.engrainMapUnitId)?.floor_id;
    return floorId && floors.some((floor) => floor.id === floorId) && artworkIds.has(match.engrainMapUnitId)
      ? [{ ...match, floorId }]
      : [];
  });
}

export const filterMapUnits = (units: Array<LocatedUnit>, floorId: string, beds: number | null): Array<LocatedUnit> =>
  units.filter((match) => match.floorId === floorId && (beds === null || match.unit.beds === beds));
